import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, Video, Phone, ShieldCheck } from "lucide-react";
import { CLINIC_PHONE, CLINIC_LOCATION_NAME, CLINIC_ADDRESS, CLINIC_MAP_URL } from "@/lib/mailto";
import { ScopeLogo } from "@/components/brand/ScopeLogo";

export function Footer() {
    const { t } = useTranslation();
    return (
        <footer
            data-testid="site-footer"
            className="mt-16 border-t border-[color:var(--border-default)] bg-white"
        >
            <div className="container-page py-12 grid gap-10 md:grid-cols-3">
                <div>
                    <div className="flex items-center gap-2.5 mb-3">
                        <ScopeLogo size={36} />
                        <span className="font-display text-lg font-semibold text-[color:var(--brand-navy)]">
                            {t("hero.brandLine1")} {t("hero.brandLine2")}
                        </span>
                    </div>
                    <p className="text-sm text-[color:var(--text-muted)] max-w-sm">
                        {t("footer.tagline")}
                    </p>
                    <div className="mt-5 space-y-2 text-sm text-[color:var(--text-muted)]">
                        <a
                            href={CLINIC_MAP_URL}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="flex items-start gap-2 hover:text-[color:var(--brand-navy)]"
                            data-testid="footer-location"
                        >
                            <MapPin className="h-4 w-4 mt-0.5 text-[color:var(--brand-teal-2)] shrink-0" />
                            <span>
                                <span className="block font-medium text-[color:var(--brand-navy)]">
                                    {CLINIC_LOCATION_NAME}
                                </span>
                                <span className="block">{CLINIC_ADDRESS}</span>
                            </span>
                        </a>
                        <div className="flex items-start gap-2">
                            <Video className="h-4 w-4 mt-0.5 text-[color:var(--brand-teal-2)]" />
                            <span>{t("footer.virtualAlso")}</span>
                        </div>
                        <a href={`tel:${CLINIC_PHONE.replace(/[^\d+]/g, "")}`} className="flex items-center gap-2 hover:text-[color:var(--brand-navy)]">
                            <Phone className="h-4 w-4 text-[color:var(--brand-teal-2)]" />
                            <span>{CLINIC_PHONE}</span>
                        </a>
                    </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 md:col-span-2">
                    <FooterCol
                        title={t("footer.columns.care")}
                        links={[
                            { to: "/services", label: t("footer.links.services") },
                            { to: "/conditions", label: t("footer.links.conditions") },
                            { to: "/technology", label: t("footer.links.technology") },
                            { to: "/preventive-care", label: t("footer.links.preventiveCare") },
                        ]}
                    />
                    <FooterCol
                        title={t("footer.columns.patients")}
                        links={[
                            { to: "/resources", label: t("footer.links.patientResources") },
                            { to: "/faq", label: t("footer.links.faq") },
                            { to: "/education", label: t("footer.links.educationLibrary") },
                            { to: "/resources/bowel-prep-guide", label: t("footer.links.bowelPrepGuide") },
                        ]}
                    />
                    <FooterCol
                        title={t("footer.columns.clinic")}
                        links={[
                            { to: "/about", label: t("footer.links.aboutDrKichian") },
                            { to: "/virtual-consultations", label: t("footer.links.virtualConsultations") },
                            { to: "/referrals", label: t("footer.links.referringPhysicians") },
                        ]}
                    />
                </div>
            </div>
            <div className="border-t border-[color:var(--border-default)]">
                <div className="container-page py-4 flex flex-col md:flex-row items-center justify-between gap-3">
                    <p className="text-xs text-[color:var(--text-soft)]">
                        &copy; {new Date().getFullYear()} {t("hero.brandLine1")} {t("hero.brandLine2")}. {t("footer.allRights")}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-[color:var(--text-soft)]">
                        <span className="flex items-center gap-1.5">
                            <ShieldCheck className="h-3.5 w-3.5 text-[color:var(--brand-teal-2)]" />
                            {t("footer.privacy")}
                        </span>
                        <span>{t("footer.educationalDisclaimer")}</span>
                    </div>
                </div>
            </div>
        </footer>
    );
}

function FooterCol({ title, links }) {
    return (
        <div>
            <h4 className="text-sm font-semibold text-[color:var(--brand-navy)] mb-3">{title}</h4>
            <ul className="space-y-2">
                {links.map((l) => (
                    <li key={l.to + l.label}>
                        <Link
                            to={l.to}
                            className="text-sm text-[color:var(--text-muted)] hover:text-[color:var(--brand-navy)]"
                        >
                            {l.label}
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
}
